/**
 * Battle sessions: backend-issued seed, lockstep input relay with turn ownership, state-hash checkpoints
 * (desync detection), finish with consensus + authoritative validation, replay creation.
 *
 * Solo sessions (`session.start`) serve single-player battles whose result the backend should know about (the legacy
 * session API; the game's own solo battles go through player.command battle.start / battle.finish, services/battles.ts).
 *
 * Online sessions (`room.start`, design D5) run a "just" battle between the room's players in lockstep:
 *  - the server builds the battle's init from the room (SessionInfo.online: OnlineBattleInit, src/session/online.ts:
 *    the room's PvP map by both mapId and mapIndex, its teams) and issues the seed; every client and the server's
 *    battle simulator build the same battle from it in the neutral battle context;
 *  - input is relayed per turn; a batch must be contiguous, come from the participant that controls the turn's team
 *    and hold only input an online battle allows (checkOnlineFrame: known weapons, clicks inside the map);
 *  - turns follow the simulation: the owner ends its turn (session.endTurn) in the tick in which its simulation started
 *    the next team's move. A turn whose owner misses the deadline, stays disconnected beyond the grace period or
 *    forfeits is FORCED: the server closes its input after the confirmed tick E (session.turn with a null team) and
 *    the participant that controls the team of the next move reports its tick and team (session.endTurn with
 *    `forcedEnd: E`); the first well-formed report wins, the final re-simulation catches lies;
 *  - a turn is bounded in wall-clock time from its start: its owner gets the turn time plus the grace after every
 *    batch of input, but never more than twice the turn time plus the grace from the turn's start (the play and the
 *    weapon sheet / scanner freezes a client allows per move), so an owner that trickles input cannot keep the turn;
 *  - checkpoints (session.hash) are accepted for ticks from 1 up to a little beyond the confirmed input and compared
 *    only once the tick is confirmed; a desync is reported to everyone (session.desync), but only the server decides
 *    it: a participant whose own simulation disagrees disputes the battle (session.dispute) and the server re-simulates
 *    the log known so far with checkpoints. The participants the simulation contradicts (a checkpoint hash that differs
 *    from the simulated one, or a turn end where the simulation did not hand the battle over; the disputing
 *    participant itself when nothing else differs) lose; the battle ends ('rejected', SessionOutcome.diverged), the
 *    only other participant wins. A liar cannot win by making an honest client give up;
 *  - every participant reports its result and final hash (session.finish); the server re-simulates the whole log in its
 *    battle simulator (asynchronously: the last report may get 'verifying', the verdict arrives as session.ended):
 *    'verified' when the simulation equals the participants' consensus and the log's turn structure matches the
 *    simulated moves, 'rejected' otherwise (the SIMULATED winner is the result), 'unverified' only without a working
 *    simulator. The battle is stored as a replay.
 * Every session event carries the session's monotonic version (SessionInfo.version) besides the hub's event seq.
 *
 * Persistence and recovery: active sessions are written (LiveSessionDoc, collection live_sessions) at every turn
 * change, forced turn, finish report and forfeit, the input between them at most every persistIntervalMs and at
 * shutdown (flush), and restored at startup: every participant counts as disconnected from that moment and gets the
 * disconnect grace period to come back (deadlines are re-armed), a session whose reports were all in is verified
 * again. An ended session moves to the `battles` collection in one batch.
 */
import { randomId, randomInt } from '../../../shared/random';
import type { JsonValue } from '../../../shared/json';
import { BackendError } from '../../../shared/protocol/errors';
import type { PayloadOf, ResultOf } from '../../../shared/protocol/messages';
import type {
  BattleInit,
  BattleResult,
  OnlineBattleInit,
  OnlineBattleTeam,
  PlayerId,
  Room,
  SessionCheckpoint,
  SessionInfo,
  SessionOutcome,
  SessionParticipant,
  ValidationVerdict,
} from '../../../shared/protocol/model';
import { DELETED_PLAYER_ID, DELETED_PLAYER_NAME } from '../../../shared/protocol/model';
import { CommandLog, type CommandLogData } from '../../../session/command-log';
import { ONLINE_TURN_TIME_SEC, type TurnViolation, checkOnlineFrame, checkTurnOwnership, findTurnViolation, onlineMapOf, withOnlineMap } from '../../../session/online';
import type { BattleSimulator, SimulatedMove, SimulationResult } from '../battle-sim';
import { weaponIds } from '../game-rules';
import type { ConnectionContext } from '../hub';
import { LiveDocWriter } from '../live-writer';
import { BattleArchive } from './battle-archive';
import { deleteOp, putOp } from '../storage';
import type { AuthoritativeValidator } from '../validation';
import type { BattleOutcomeLookup } from './assault';
import type { AuthService } from './auth';
import { type ServiceDeps, C } from './deps';
import type { ReplayService } from './replays';

interface FinishReport {
  result: BattleResult;
  finalHash: string | null;
  log: CommandLogData | null;
  at: number;
}

/** A participant disputed the online battle (session.dispute): the server decides it by a re-simulation. */
interface DisputeRecord {
  by: PlayerId;
  tick: number;
  reason: string | null;
  at: number;
  /** The command log when the dispute arrived: the re-simulation runs it up to its last known tick. */
  log: CommandLogData;
}

interface SessionState {
  info: SessionInfo;
  log: CommandLog;
  solo: boolean;
  hashes: Map<number, Map<PlayerId, string>>;
  desyncTicks: Set<number>;
  reports: Map<PlayerId, FinishReport>;
  eliminated: Set<number>;
  lastProgressAt: number;
  /** Wall-clock start of the current turn (or of the wait for a forced turn's report): the turn's budget counts from it. */
  turnStartedAt: number;
  /** Who reported the end of each turn (a dispute blames the reporter of a turn end the simulation contradicts). */
  turnEnds: Map<number, PlayerId>;
  dispute: DisputeRecord | null;
  firstFinishAt: number | null;
  endedAt: number | null;
  finalizing: boolean;
  /** Wall-clock time the current turn's input was closed (a forced turn waiting for its report), null otherwise. */
  forcedSince: number | null;
  /** Changed since the last write of its live document. */
  dirty: boolean;
  persistedAt: number;
  /** The background verification or dispute resolution of the session while one runs (the account deletion waits for it). */
  running: Promise<void> | null;
}

/** An ended session as stored in C.battles (id = sessionId). */
export interface BattleDoc {
  sessionId: string;
  info: SessionInfo;
  log: CommandLogData;
}

/** The stored form of an active session (collection live_sessions, id = sessionId). */
export interface LiveSessionDoc {
  format: 'fwp-live-session-v1';
  sessionId: string;
  info: SessionInfo;
  log: CommandLogData;
  solo: boolean;
  hashes: { tick: number; hashes: Record<PlayerId, string> }[];
  desyncTicks: number[];
  reports: { playerId: PlayerId; result: BattleResult; finalHash: string | null; log: CommandLogData | null; at: number }[];
  eliminated: number[];
  lastProgressAt: number;
  firstFinishAt: number | null;
  forcedSince: number | null;
  /** Reporters of the turn ends (absent in documents of older versions). */
  turnEnds?: { turn: number; playerId: PlayerId }[];
  /** A dispute waiting for its resolution (verified again after a restart). */
  dispute?: DisputeRecord | null;
  savedAt: number;
}

export const LIVE_SESSION_FORMAT = 'fwp-live-session-v1';

/**
 * The online init of a stored session with both map fields: a session stored by an older version names its map by
 * mapIndex only (null stays null; a record without a known map is kept as it is).
 */
function onlineInitOf(online: OnlineBattleInit | null | undefined): OnlineBattleInit | null {
  if (!online) return null;
  return (withOnlineMap(online) as OnlineBattleInit | null) ?? online;
}

/**
 * What the end of a session adds to its SessionInfo for the anti-cheat: each participant's finish report and, when the
 * server re-simulated the online battle, the simulated winner and final hash. `forced`: the server ended the session
 * with a result of its own (a forfeit, an abandoned battle, an operator, a dispute), not with what the participants
 * reported.
 */
export interface SessionEndFacts {
  reports: { playerId: PlayerId; winnerTeam: number; finalHash: string | null; at: number }[];
  simulated: { winnerTeam: number; finalHash: string } | null;
  forced: boolean;
}

export type SessionEndedListener = (info: SessionInfo, facts: SessionEndFacts) => void;

/** Online input the server refused (checkOnlineFrame: an unknown weapon, a click outside the map, a surrender). */
export interface SessionInputRefusal {
  sessionId: string;
  playerId: PlayerId;
  tick: number;
  reason: string;
  at: number;
}

export type SessionInputRefusalListener = (r: SessionInputRefusal) => void;

/** TURNTIME: 20 s in hardcore (skill >= 10) except assault/just, otherwise 45 s (proc.as:472-479). */
export function turnTimeFor(init: BattleInit): number {
  return (init.skill ?? 0) >= 10 && init.mode !== 'assault' && init.mode !== 'just' ? 20 : 45;
}

/** Transient simulator failures are retried after these pauses before the battle stays unverified. */
const SIM_RETRY_DELAYS_MS = [1_000, 5_000];

/** Empty ticks the simulation of an online battle may run past the end of its log before it counts as endless. */
const SIM_TICKS_PAST_LOG = 10_000;

/**
 * Battle ticks per second a checkpoint may run ahead of the confirmed input, over a turn's whole wall-clock budget: the
 * owner of a turn hashes its move starts before its input batch is confirmed (the game runs 50 ticks per second).
 */
const HASH_AHEAD_TICKS_PER_SEC = 60;

export interface SessionServiceOptions {
  /** Re-simulates finished online battles; null = they stay 'unverified'. */
  simulator?: BattleSimulator | null;
}

export class SessionService implements BattleOutcomeLookup {
  private readonly sessions = new Map<string, SessionState>();
  /** session.start budget per player (config.sessions.soloStarts): tokens left and when they were counted. */
  private readonly soloStarts = new Map<PlayerId, { tokens: number; at: number }>();
  private readonly endedListeners = new Set<SessionEndedListener>();
  private readonly refusalListeners = new Set<SessionInputRefusalListener>();
  private readonly writer: LiveDocWriter;
  private readonly simulator: BattleSimulator | null;
  /** Shutdown began: running verifications stop without writing (the restored session verifies again). */
  private stopped = false;
  private readonly verifications = new Set<Promise<void>>();
  /** The stored ended battles (collection battles): their index and retention (./battle-archive.ts). */
  readonly archive: BattleArchive;

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly replays: ReplayService,
    private readonly validator: AuthoritativeValidator,
    opts: SessionServiceOptions = {},
  ) {
    this.writer = new LiveDocWriter(d.storage, d.logger);
    this.simulator = opts.simulator ?? null;
    this.archive = new BattleArchive(d);
  }

  onEnded(l: SessionEndedListener): () => void {
    this.endedListeners.add(l);
    return () => this.endedListeners.delete(l);
  }

  /** Calls `l` with every online input batch refused for input an online battle does not allow (the anti-cheat). */
  onInputRefused(l: SessionInputRefusalListener): () => void {
    this.refusalListeners.add(l);
    return () => this.refusalListeners.delete(l);
  }

  /** Active sessions (/health). */
  activeCount(): number {
    let n = 0;
    for (const s of this.sessions.values()) if (s.info.state === 'active') n++;
    return n;
  }

  /** Operator API (admin console): the sessions this process knows (running ones and recently ended ones). */
  listAll(): SessionInfo[] {
    return [...this.sessions.values()].map((s) => s.info);
  }

  /**
   * Operator API (admin console): ends a running session without a result (winner 0, reason 'ended by an operator';
   * session.ended to its participants, the battle stored like an abandoned one). NOT_FOUND 'session_not_found' for an
   * unknown session, INVALID_STATE 'session_ended' for one that already ended.
   */
  async endByOperator(sessionId: string): Promise<SessionInfo> {
    const s = this.sessions.get(sessionId);
    if (!s) throw new BackendError('NOT_FOUND', 'Session not found', { reason: 'session_not_found', sessionId });
    if (s.info.state !== 'active' || s.finalizing) throw new BackendError('INVALID_STATE', 'Session has ended', { reason: 'session_ended', sessionId });
    await this.finalize(s, { winnerTeam: 0 }, 'ended by an operator');
    return s.info;
  }

  /**
   * Account deletion (./account-deletion.ts): the player leaves every session this process knows (running ones and
   * recently ended ones). First its place is anonymized: DELETED_PLAYER_ID / DELETED_PLAYER_NAME in the participants
   * and the online teams, and in the session's per-player records (checkpoints, reports, turn ends, a dispute, the
   * outcome's `diverged`), so nothing the session stores or sends from now on names the player. Then a running session
   * is forfeited for it as by session.forfeit: a solo session ends at once and, with nobody else in it, is stored
   * neither as a battle nor as a replay; an online one goes on (or ends) for the others. A verification or dispute
   * resolution of such a session that is running is awaited, so its battle record and replay are written before this
   * resolves (the deletion then looks at the stored battles). Returns the ids of the sessions it changed.
   */
  async removePlayer(playerId: PlayerId): Promise<string[]> {
    this.soloStarts.delete(playerId);
    const touched: string[] = [];
    for (const s of [...this.sessions.values()]) {
      const part = this.anonymize(s, playerId);
      if (!part) continue;
      touched.push(s.info.sessionId);
      if (s.info.state === 'active' && !s.finalizing) await this.forfeitParticipant(s, part);
    }
    for (const id of touched) {
      const s = this.sessions.get(id);
      while (s?.running) await s.running.catch(() => undefined);
      if (s && s.info.state === 'active' && s.dirty) void this.persist(s);
    }
    await this.writer.idle();
    return touched;
  }

  /** Account deletion: the ended sessions this process keeps no longer point to the deleted replays `ids`. */
  forgetReplays(ids: ReadonlySet<string>): void {
    for (const s of this.sessions.values()) {
      const outcome = s.info.outcome;
      if (outcome?.replayId && ids.has(outcome.replayId)) outcome.replayId = null;
    }
  }

  /** Replaces the player by DELETED_PLAYER_ID in the session (see removePlayer); its participant, or null. */
  private anonymize(s: SessionState, playerId: PlayerId): SessionParticipant | null {
    const part = s.info.participants.find((p) => p.playerId === playerId);
    if (!part) return null;
    part.playerId = DELETED_PLAYER_ID;
    part.displayName = DELETED_PLAYER_NAME;
    for (const t of s.info.online?.teams ?? []) if (t.playerId === playerId) t.playerId = DELETED_PLAYER_ID;
    for (const m of s.hashes.values()) {
      const h = m.get(playerId);
      if (h === undefined) continue;
      m.delete(playerId);
      m.set(DELETED_PLAYER_ID, h);
    }
    const report = s.reports.get(playerId);
    if (report) {
      s.reports.delete(playerId);
      s.reports.set(DELETED_PLAYER_ID, report);
    }
    for (const [turn, pid] of s.turnEnds) if (pid === playerId) s.turnEnds.set(turn, DELETED_PLAYER_ID);
    if (s.dispute?.by === playerId) s.dispute.by = DELETED_PLAYER_ID;
    const outcome = s.info.outcome;
    if (outcome?.diverged) outcome.diverged = outcome.diverged.map((p) => (p === playerId ? DELETED_PLAYER_ID : p));
    s.dirty = true;
    return part;
  }

  /**
   * Takes one session.start from the player's budget (config.sessions.soloStarts: a burst, then `perHour`). Every
   * solo session that ends is stored for good (collection battles, and a replay), so starting and forfeiting in a loop
   * must not be free. Returns the milliseconds until the next start is allowed, or 0 when this one may go.
   */
  private takeSoloStart(playerId: PlayerId, now: number): number {
    const { burst, perHour } = this.d.config.sessions.soloStarts;
    const perMs = perHour / 3_600_000;
    const b = this.soloStarts.get(playerId) ?? { tokens: burst, at: now };
    b.tokens = Math.min(burst, b.tokens + (now - b.at) * perMs);
    b.at = now;
    this.soloStarts.set(playerId, b);
    if (this.soloStarts.size > 10_000) {
      for (const [id, x] of this.soloStarts) if (x.tokens + (now - x.at) * perMs >= burst) this.soloStarts.delete(id);
    }
    if (b.tokens < 1) return Math.max(1, Math.ceil((1 - b.tokens) / Math.max(perMs, 1e-12)));
    b.tokens -= 1;
    return 0;
  }

  /** Ids of the player's running solo sessions (session.start). */
  private runningSolo(playerId: PlayerId): string[] {
    const ids: string[] = [];
    for (const s of this.sessions.values()) {
      if (s.solo && s.info.state === 'active' && !s.finalizing && s.info.participants[0]?.playerId === playerId) ids.push(s.info.sessionId);
    }
    return ids;
  }

  /** The session is known and still running. */
  isActive(sessionId: string): boolean {
    return this.sessions.get(sessionId)?.info.state === 'active';
  }

  private graceMs(): number {
    return this.d.config.sessions.disconnectGraceMs;
  }

  private newState(info: SessionInfo, solo: boolean): SessionState {
    const s: SessionState = {
      info,
      log: CommandLog.create(info.currentTeam, 0),
      solo,
      hashes: new Map(),
      desyncTicks: new Set(),
      reports: new Map(),
      eliminated: new Set(),
      lastProgressAt: info.createdAt,
      turnStartedAt: info.createdAt,
      turnEnds: new Map(),
      dispute: null,
      firstFinishAt: null,
      endedAt: null,
      finalizing: false,
      forcedSince: null,
      dirty: true,
      persistedAt: 0,
      running: null,
    };
    this.sessions.set(info.sessionId, s);
    return s;
  }

  /**
   * Latest wall-clock time the current turn may run to: twice the turn time (the play, and the weapon sheet / scanner
   * freezes a client allows per move) plus the grace, from the turn's start. Input does not move it.
   */
  private turnCap(s: SessionState): number {
    return s.turnStartedAt + (2 * s.info.turnTimeSec + this.d.config.sessions.turnGraceSec) * 1000;
  }

  /** The turn's deadline after progress at `now`: the turn time plus the grace, within the turn's budget. */
  private deadline(s: SessionState, now: number): number | null {
    return s.solo ? null : Math.min(now + (s.info.turnTimeSec + this.d.config.sessions.turnGraceSec) * 1000, this.turnCap(s));
  }

  /** A change of the session: its version goes up and its live document is due. */
  private bump(s: SessionState): number {
    s.info.version += 1;
    s.dirty = true;
    return s.info.version;
  }

  // ------------------------------------------------------------------------------------------------------
  // Persistence

  private liveDoc(s: SessionState): LiveSessionDoc {
    return {
      format: LIVE_SESSION_FORMAT,
      sessionId: s.info.sessionId,
      info: s.info,
      log: s.log.toJSON(),
      solo: s.solo,
      hashes: [...s.hashes.entries()].map(([tick, m]) => ({ tick, hashes: Object.fromEntries(m) })),
      desyncTicks: [...s.desyncTicks],
      reports: [...s.reports.entries()].map(([playerId, r]) => ({ playerId, ...r })),
      eliminated: [...s.eliminated],
      lastProgressAt: s.lastProgressAt,
      firstFinishAt: s.firstFinishAt,
      forcedSince: s.forcedSince,
      turnEnds: [...s.turnEnds.entries()].map(([turn, playerId]) => ({ turn, playerId })),
      dispute: s.dispute,
      savedAt: this.d.clock.now(),
    };
  }

  /** Writes the session's live document now (an ended session's document is removed by finalize). */
  private persist(s: SessionState): Promise<void> {
    s.dirty = false;
    s.persistedAt = this.d.clock.now();
    const id = s.info.sessionId;
    return this.writer.schedule(`session:${id}`, () => {
      if (s.info.state !== 'active') return [];
      return [putOp(C.liveSessions, id, this.liveDoc(s))];
    });
  }

  /** Writes every changed active session (flush hook: shutdown, tests) and waits for the writes. */
  async flush(): Promise<void> {
    for (const s of this.sessions.values()) if (s.info.state === 'active' && s.dirty) void this.persist(s);
    await this.writer.idle();
  }

  /** Shutdown: verifications that are still running stop without writing (restored sessions verify again). */
  stop(): void {
    this.stopped = true;
  }

  /** Waits for the verifications that are running (tests). */
  async settled(): Promise<void> {
    while (this.verifications.size > 0) await Promise.all([...this.verifications]);
    await this.writer.idle();
  }

  /**
   * Restores the active sessions of the live_sessions collection (startup). Every participant of a restored online
   * session counts as disconnected from now on: it has the disconnect grace period to come back before its turns are
   * forced (the turn deadline is re-armed to at least now + grace). A session whose reports were all in is verified
   * again. Documents of an unknown format or with an invalid log are skipped (logged).
   */
  async restore(): Promise<void> {
    const now = this.d.clock.now();
    let restored = 0;
    for (const doc of await this.d.storage.list<LiveSessionDoc>(C.liveSessions)) {
      if (!doc || doc.format !== LIVE_SESSION_FORMAT || typeof doc.sessionId !== 'string' || !doc.info) {
        this.d.logger.warn('live session document skipped', { sessionId: (doc as { sessionId?: unknown })?.sessionId ?? null });
        continue;
      }
      let log: CommandLog;
      try {
        log = CommandLog.fromJSON(doc.log);
      } catch (err) {
        this.d.logger.warn('live session with an invalid log skipped', { sessionId: doc.sessionId, err });
        continue;
      }
      const info = doc.info;
      if (info.state !== 'active') continue;
      info.version = (info.version ?? 0) + 1;
      info.online = onlineInitOf(info.online);
      if (info.init?.teams && info.online) info.init.teams = withOnlineMap(info.init.teams) ?? info.init.teams;
      info.inputClosedAt ??= null;
      const s: SessionState = {
        info,
        log,
        solo: doc.solo,
        hashes: new Map((doc.hashes ?? []).map((h) => [h.tick, new Map(Object.entries(h.hashes))])),
        desyncTicks: new Set(doc.desyncTicks ?? []),
        reports: new Map((doc.reports ?? []).map((r) => [r.playerId, { result: r.result, finalHash: r.finalHash, log: r.log ?? null, at: r.at }])),
        eliminated: new Set(doc.eliminated ?? []),
        lastProgressAt: now,
        // The restart is not the owner's fault: the current turn gets its whole budget again.
        turnStartedAt: now,
        turnEnds: new Map((doc.turnEnds ?? []).map((e) => [e.turn, e.playerId])),
        dispute: doc.dispute ?? null,
        firstFinishAt: doc.firstFinishAt ?? null,
        endedAt: null,
        finalizing: false,
        forcedSince: doc.forcedSince ?? null,
        dirty: true,
        persistedAt: 0,
        running: null,
      };
      if (!s.solo) {
        for (const p of info.participants) {
          p.connected = false;
          p.disconnectedAt = now;
        }
        if (info.deadlineAt !== null) info.deadlineAt = Math.max(info.deadlineAt, now + this.graceMs());
        if (s.forcedSince !== null) s.forcedSince = now;
        if (s.firstFinishAt !== null) s.firstFinishAt = now;
      }
      this.sessions.set(info.sessionId, s);
      restored++;
      const pending = info.participants.filter((x) => !x.finished && !x.forfeited);
      if (!s.solo && s.dispute) this.startResolution(s);
      else if (!s.solo && s.reports.size > 0 && pending.length === 0) this.startVerification(s);
    }
    if (restored > 0) this.d.logger.info('battle sessions restored', { sessions: restored });
  }

  // ------------------------------------------------------------------------------------------------------
  // Start

  /** The seed of a new battle: random, or the fixed one of BATTLE_SEED (a test and development switch). */
  private battleSeed(): number {
    return this.d.fixedBattleSeed ?? randomInt(this.d.rng, 400);
  }

  /**
   * Solo battle session: the backend issues the seed (replaces `true_random(0,399)`, proc.as:494). A player runs at
   * most config.sessions.maxSoloPerPlayer of them at once (each one lives in memory and in live_sessions until it
   * ends or soloTtlMs passes): one more is INVALID_STATE 'too_many_sessions' naming the running ones, which the client
   * finishes or forfeits first. Starts are also limited per player in time (takeSoloStart): RATE_LIMITED 'rate_limited'.
   */
  async start(playerId: PlayerId, init: BattleInit): Promise<SessionInfo> {
    const a = await this.auth.requireAccount(playerId);
    // No await between this count and newState(): concurrent starts cannot pass the limit together.
    const running = this.runningSolo(playerId);
    const limit = this.d.config.sessions.maxSoloPerPlayer;
    if (running.length >= limit) {
      throw new BackendError('INVALID_STATE', `At most ${limit} solo sessions may run at once: finish or forfeit one first`, { reason: 'too_many_sessions', limit, sessionIds: running });
    }
    const now = this.d.clock.now();
    const retryAfterMs = this.takeSoloStart(playerId, now);
    if (retryAfterMs > 0) throw new BackendError('RATE_LIMITED', 'Too many solo sessions started: retry later', { reason: 'rate_limited', retryAfterMs });
    const teamCount = init.teams?.teams.length ?? 1;
    const info: SessionInfo = {
      sessionId: randomId(this.d.rng, 's_', 10),
      mode: init.mode,
      stage: init.stage,
      roomId: null,
      seed: this.battleSeed(),
      turnTimeSec: turnTimeFor(init),
      init,
      online: null,
      participants: [{ playerId, displayName: a.displayName, teams: [1], connected: true, disconnectedAt: null, finished: false, forfeited: false }],
      teamOrder: Array.from({ length: teamCount }, (_, i) => i + 1),
      state: 'active',
      turn: 0,
      currentTeam: 1,
      turnStartTick: 0,
      inputClosedAt: null,
      deadlineAt: null,
      createdAt: now,
      outcome: null,
      version: 1,
    };
    const s = this.newState(info, true);
    await this.persist(s);
    this.d.logger.info('session started', { sessionId: info.sessionId, mode: init.mode, solo: true });
    return info;
  }

  /**
   * Online session of a room ("just" mode): the battle init from the room's settings and players (a team without a
   * player is controlled by the host), a server seed, turn 0 of team 1 (the original always starts with team 1).
   */
  async createFromRoom(room: Room): Promise<SessionInfo> {
    const now = this.d.clock.now();
    const participants: SessionParticipant[] = room.players.map((p) => ({
      playerId: p.playerId,
      displayName: p.displayName,
      teams: [p.team],
      connected: p.connected,
      disconnectedAt: p.connected ? null : (p.disconnectedAt ?? now),
      finished: false,
      forfeited: false,
    }));
    const teams: OnlineBattleTeam[] = room.settings.teams.map((t, i) => ({
      team: i + 1,
      name: t.name,
      color: t.color,
      worms: t.worms,
      cpu_level: 0,
      playerId: room.players.find((p) => p.team === i + 1)?.playerId ?? null,
    }));
    const host = participants.find((p) => p.playerId === room.hostId) ?? participants[0];
    for (const t of teams) if (t.playerId === null) host.teams.push(t.team);
    for (const p of participants) p.teams.sort((a, b) => a - b);
    const map = onlineMapOf(room.settings);
    if (!map) throw new BackendError('INTERNAL', 'The room names no PvP map', { reason: 'internal_error', roomId: room.roomId });
    const online: OnlineBattleInit = { kind: 'online', mode: 'just', mapId: map.mapId, mapIndex: map.mapIndex, teams, turnTimeSec: ONLINE_TURN_TIME_SEC, neutral: true };
    const init: BattleInit = { mode: 'just', stage: map.mapIndex, mapId: map.mapId, teams: { mapId: map.mapId, mapIndex: map.mapIndex, teams: room.settings.teams } };
    const info: SessionInfo = {
      sessionId: randomId(this.d.rng, 's_', 10),
      mode: 'just',
      stage: map.mapIndex,
      roomId: room.roomId,
      seed: this.battleSeed(),
      turnTimeSec: online.turnTimeSec,
      init,
      online,
      participants,
      teamOrder: teams.map((t) => t.team),
      state: 'active',
      turn: 0,
      currentTeam: 1,
      turnStartTick: 0,
      inputClosedAt: null,
      deadlineAt: null,
      createdAt: now,
      outcome: null,
      version: 1,
    };
    const s = this.newState(info, false);
    info.deadlineAt = this.deadline(s, now);
    await this.persist(s);
    this.d.hub.send(
      participants.map((p) => p.playerId),
      'session.started',
      { session: info },
    );
    this.d.logger.info('session started', { sessionId: info.sessionId, roomId: room.roomId, players: participants.length, seed: info.seed });
    return info;
  }

  // ------------------------------------------------------------------------------------------------------
  // Requests

  private require(sessionId: string): SessionState {
    const s = this.sessions.get(sessionId);
    if (!s) throw new BackendError('NOT_FOUND', 'Session not found', { reason: 'session_not_found', sessionId });
    return s;
  }

  private participant(s: SessionState, playerId: PlayerId): SessionParticipant {
    const p = s.info.participants.find((x) => x.playerId === playerId);
    if (!p) throw new BackendError('FORBIDDEN', 'You are not a participant of this session', { reason: 'not_participant', sessionId: s.info.sessionId });
    return p;
  }

  /** The participant that controls `team` (online sessions: the host controls the teams without a player). */
  private controller(s: SessionState, team: number): SessionParticipant | null {
    return s.info.participants.find((p) => p.teams.includes(team)) ?? null;
  }

  private requireActive(s: SessionState): void {
    if (s.info.state !== 'active') throw new BackendError('INVALID_STATE', 'Session has ended', { reason: 'session_ended', sessionId: s.info.sessionId });
  }

  private turnForced(s: SessionState): never {
    const cur = s.log.current;
    throw new BackendError('CONFLICT', `Turn ${cur.turn} was forced: its input is closed after tick ${cur.forcedAt}`, {
      reason: 'turn_forced',
      forcedAt: cur.forcedAt ?? null,
      currentTurn: cur.turn,
      team: cur.team,
    });
  }

  private requireTurnOwner(s: SessionState, p: SessionParticipant, turn: number): void {
    if (turn !== s.log.current.turn) {
      throw new BackendError('CONFLICT', `Turn ${turn} is not the current turn ${s.log.current.turn}`, { reason: 'not_current_turn', currentTurn: s.log.current.turn, team: s.info.currentTeam });
    }
    if (!s.solo && !p.teams.includes(s.info.currentTeam)) {
      throw new BackendError('FORBIDDEN', `Team ${s.info.currentTeam} is not yours`, { reason: 'not_your_team', currentTeam: s.info.currentTeam });
    }
    if (p.forfeited) throw new BackendError('INVALID_STATE', 'You forfeited this session', { reason: 'forfeited' });
  }

  private others(s: SessionState, playerId: PlayerId): PlayerId[] {
    return s.info.participants.filter((p) => p.playerId !== playerId).map((p) => p.playerId);
  }

  private all(s: SessionState): PlayerId[] {
    return s.info.participants.map((p) => p.playerId);
  }

  private sync(s: SessionState): void {
    const cur = s.log.current;
    s.info.turn = cur.turn;
    s.info.currentTeam = cur.team;
    s.info.turnStartTick = cur.startTick;
    s.info.inputClosedAt = cur.endTick === null && cur.forcedAt !== undefined ? cur.forcedAt : null;
  }

  /** The hashes every reporting participant agreed on, by tick. */
  private checkpoints(s: SessionState): SessionCheckpoint[] {
    const out: SessionCheckpoint[] = [];
    for (const [tick, m] of [...s.hashes.entries()].sort((a, b) => a[0] - b[0])) {
      const vals = new Set(m.values());
      if (vals.size === 1) out.push({ tick, hash: [...vals][0] });
    }
    return out;
  }

  /**
   * session.get: a live session, or an ended one that is no longer in memory (after a restart or the keep period)
   * from its stored battle document.
   */
  async getAny(playerId: PlayerId, sessionId: string): Promise<ResultOf<'session.get'>> {
    if (this.sessions.has(sessionId)) return this.get(playerId, sessionId);
    const doc = await this.d.storage.get<BattleDoc>(C.battles, sessionId);
    if (!doc || !doc.info) throw new BackendError('NOT_FOUND', 'Session not found', { reason: 'session_not_found', sessionId });
    if (!doc.info.participants.some((p) => p.playerId === playerId)) throw new BackendError('FORBIDDEN', 'You are not a participant of this session', { reason: 'not_participant', sessionId });
    const log = CommandLog.fromJSON(doc.log);
    return { session: { ...doc.info, online: onlineInitOf(doc.info.online), version: doc.info.version ?? 0, inputClosedAt: null }, log: log.toJSON(), turns: this.turnSummaries(log), hashes: [], snapshotTick: null };
  }

  private turnSummaries(log: CommandLog): ResultOf<'session.get'>['turns'] {
    return log.turns.map((t) => ({
      turn: t.turn,
      team: t.team,
      startTick: t.startTick,
      endTick: t.endTick,
      throughTick: t.throughTick,
      forcedAt: t.forcedAt ?? null,
      commands: t.commands.length,
    }));
  }

  get(playerId: PlayerId, sessionId: string): ResultOf<'session.get'> {
    const s = this.require(sessionId);
    this.participant(s, playerId);
    return {
      session: s.info,
      log: s.log.toJSON(),
      turns: this.turnSummaries(s.log),
      hashes: this.checkpoints(s),
      snapshotTick: null,
    };
  }

  input(playerId: PlayerId, p: PayloadOf<'session.input'>, ctx: ConnectionContext | null): ResultOf<'session.input'> {
    const s = this.require(p.sessionId);
    const part = this.participant(s, playerId);
    this.requireActive(s);
    this.requireTurnOwner(s, part, p.turn);
    const cur = s.log.current;
    if (cur.forcedAt !== undefined) this.turnForced(s);
    if (p.fromTick > cur.throughTick + 1) {
      throw new BackendError('CONFLICT', `Input gap: batch starts at ${p.fromTick}, confirmed through ${cur.throughTick}`, { reason: 'input_gap', throughTick: cur.throughTick });
    }
    if (p.throughTick <= cur.throughTick) return { throughTick: cur.throughTick }; // retried batch, already applied
    // Skip commands inside the already confirmed range (a retry of a batch whose response was lost).
    const fromTick = cur.throughTick + 1;
    const fresh = p.commands.filter((c) => c.tick >= fromTick);
    if (s.info.online) {
      const ids = weaponIds();
      for (const c of fresh) {
        const why = checkOnlineFrame(c.frame, s.info.online, ids);
        if (why) {
          const r: SessionInputRefusal = { sessionId: s.info.sessionId, playerId, tick: c.tick, reason: why, at: this.d.clock.now() };
          for (const l of this.refusalListeners) {
            try {
              l(r);
            } catch (err) {
              this.d.logger.error('session refusal listener failed', { sessionId: r.sessionId, err });
            }
          }
        }
        if (why) throw new BackendError('VALIDATION_FAILED', `Input of tick ${c.tick} is not allowed in an online battle: ${why}`, { reason: why, tick: c.tick });
      }
    }
    const err = s.log.append(p.turn, fresh, p.throughTick);
    if (err) throw new BackendError('VALIDATION_FAILED', err, { reason: 'invalid_input', problem: err });
    s.lastProgressAt = this.d.clock.now();
    if (!s.solo) {
      s.info.deadlineAt = this.deadline(s, s.lastProgressAt);
      const version = this.bump(s);
      this.d.hub.send(
        this.others(s, playerId),
        'session.input',
        { sessionId: s.info.sessionId, playerId, team: s.info.currentTeam, turn: p.turn, fromTick, throughTick: p.throughTick, commands: fresh, version },
        ctx ?? undefined,
      );
    } else s.dirty = true;
    return { throughTick: s.log.current.throughTick };
  }

  endTurn(playerId: PlayerId, p: PayloadOf<'session.endTurn'>, ctx: ConnectionContext | null): ResultOf<'session.endTurn'> {
    const s = this.require(p.sessionId);
    const part = this.participant(s, playerId);
    this.requireActive(s);
    // Retry of an already applied end of turn (the response was lost), or the same report as the one that won.
    const done = s.log.turns[p.turn];
    if (p.turn < s.log.current.turn && done && done.endTick === p.tick && s.log.turns[p.turn + 1]?.team === p.nextTeam) {
      const next = s.log.turns[p.turn + 1];
      return { turn: next.turn, team: next.team, startTick: next.startTick, deadlineAt: s.info.deadlineAt };
    }
    const cur = s.log.current;
    if (!s.solo && cur.forcedAt !== undefined && p.turn === cur.turn) return this.reportForcedEnd(s, part, p, ctx);
    if (p.forcedEnd !== undefined && p.turn === cur.turn) {
      throw new BackendError('VALIDATION_FAILED', `Turn ${p.turn} was not forced`, { reason: 'turn_not_forced' });
    }
    this.requireTurnOwner(s, part, p.turn);
    if (!s.solo && !s.info.teamOrder.includes(p.nextTeam)) {
      throw new BackendError('VALIDATION_FAILED', `Team ${p.nextTeam} cannot take the next turn`, { reason: 'team_not_in_battle', teamOrder: s.info.teamOrder });
    }
    if (!s.solo && p.nextTeam === cur.team) {
      throw new BackendError('VALIDATION_FAILED', `The next move belongs to another team than ${cur.team}`, { reason: 'same_team' });
    }
    const err = s.log.endTurn(p.turn, p.tick, p.nextTeam);
    if (err) throw new BackendError('VALIDATION_FAILED', err, { reason: 'invalid_turn_end', problem: err });
    s.turnEnds.set(p.turn, playerId);
    return this.turnChanged(s, playerId, p.turn, p.tick, false, ctx);
  }

  /** The report of a forced turn's end (session.endTurn with forcedEnd), see the module comment. */
  private reportForcedEnd(s: SessionState, part: SessionParticipant, p: PayloadOf<'session.endTurn'>, ctx: ConnectionContext | null): ResultOf<'session.endTurn'> {
    const cur = s.log.current;
    if (p.forcedEnd === undefined || p.forcedEnd !== cur.forcedAt) this.turnForced(s);
    if (part.forfeited) throw new BackendError('INVALID_STATE', 'You forfeited this session', { reason: 'forfeited' });
    if (!s.info.teamOrder.includes(p.nextTeam)) {
      throw new BackendError('VALIDATION_FAILED', `Team ${p.nextTeam} cannot take the next turn`, { reason: 'team_not_in_battle', teamOrder: s.info.teamOrder });
    }
    if (p.nextTeam === cur.team) throw new BackendError('VALIDATION_FAILED', `The next move belongs to another team than ${cur.team}`, { reason: 'same_team' });
    const now = this.d.clock.now();
    const ctl = this.controller(s, p.nextTeam);
    // The team's own controller reports; anybody else only when that controller cannot (gone) or did not in time.
    const mayReport =
      part.teams.includes(p.nextTeam) || !ctl || ctl.forfeited || !ctl.connected || (s.info.deadlineAt !== null && now > s.info.deadlineAt);
    if (!mayReport) throw new BackendError('FORBIDDEN', `Team ${p.nextTeam} is not yours to report`, { reason: 'not_your_team' });
    const err = s.log.endTurn(p.turn, p.tick, p.nextTeam);
    if (err) throw new BackendError('VALIDATION_FAILED', err, { reason: 'invalid_turn_end', problem: err });
    s.turnEnds.set(p.turn, part.playerId);
    this.d.logger.info('forced turn reported', { sessionId: s.info.sessionId, turn: p.turn, tick: p.tick, nextTeam: p.nextTeam, by: part.playerId });
    return this.turnChanged(s, part.playerId, p.turn, p.tick, true, ctx);
  }

  /** After an end of turn: the new turn's state and deadline, the event, and a new turn of a team nobody plays. */
  private turnChanged(s: SessionState, playerId: PlayerId, endedTurn: number, endTick: number, forced: boolean, ctx: ConnectionContext | null): ResultOf<'session.endTurn'> {
    const now = this.d.clock.now();
    this.sync(s);
    s.forcedSince = null;
    s.lastProgressAt = now;
    s.turnStartedAt = now;
    s.info.deadlineAt = this.deadline(s, now);
    const version = this.bump(s);
    if (!s.solo) {
      this.d.hub.send(
        this.others(s, playerId),
        'session.turn',
        {
          sessionId: s.info.sessionId,
          endedTurn,
          endTick,
          turn: s.info.turn,
          team: s.info.currentTeam,
          startTick: s.info.turnStartTick,
          forced,
          deadlineAt: s.info.deadlineAt,
          version,
        },
        ctx ?? undefined,
      );
      void this.persist(s);
      // A turn of a team whose controller forfeited is forced at once (nobody will play it).
      const ctl = this.controller(s, s.info.currentTeam);
      if (!ctl || ctl.forfeited) this.forceTurn(s, 'forfeited team');
    }
    return { turn: s.info.turn, team: s.info.currentTeam, startTick: s.info.turnStartTick, deadlineAt: s.info.deadlineAt };
  }

  hash(playerId: PlayerId, p: PayloadOf<'session.hash'>): ResultOf<'session.hash'> {
    const s = this.require(p.sessionId);
    this.participant(s, playerId);
    this.requireActive(s);
    const known = s.log.current.throughTick;
    if (!s.solo) {
      const ahead = (2 * s.info.turnTimeSec + this.d.config.sessions.turnGraceSec) * HASH_AHEAD_TICKS_PER_SEC;
      if (p.tick < 1 || p.tick > Math.max(known, 0) + ahead) {
        throw new BackendError('VALIDATION_FAILED', `Tick ${p.tick} is not a tick of this battle yet (input known through ${known})`, { reason: 'tick_not_reached', throughTick: known });
      }
      // The owner of a forced turn hashed a move start after the turn's closed input: its simulation ran on input the
      // server dropped (it rebuilds the battle and reports again), so the report says nothing about the battle.
      if (this.forcedOwnerTick(s, playerId, p.tick)) return { match: null };
      // The evidence of a desync is final: a participant cannot take back the checkpoint that caused it (a dispute
      // decides by the checkpoints as they were).
      const before = s.hashes.get(p.tick)?.get(playerId);
      if (s.desyncTicks.has(p.tick) && before !== undefined && before !== p.hash) return { match: false };
    }
    const m = this.storeHash(s, playerId, p.tick, p.hash);
    s.dirty = true;
    // A tick whose input is not confirmed yet (the owner's own move start) is compared once another participant, which
    // can only simulate confirmed ticks, reports it. Every tick after a forced turn's closed input is known (empty).
    const cur = s.log.current;
    const confirmed = cur.forcedAt !== undefined && cur.endTick === null ? Number.POSITIVE_INFINITY : known;
    if (!m || m.size < 2 || (!s.solo && p.tick > confirmed)) return { match: null };
    const distinct = new Set(m.values()).size;
    if (distinct > 1 && !s.desyncTicks.has(p.tick)) {
      s.desyncTicks.add(p.tick);
      const hashes = Object.fromEntries(m);
      const version = this.bump(s);
      this.d.logger.warn('session desync detected', { sessionId: s.info.sessionId, tick: p.tick });
      this.d.hub.send(this.all(s), 'session.desync', { sessionId: s.info.sessionId, tick: p.tick, hashes, version });
    }
    return { match: distinct === 1 };
  }

  /**
   * Stores a checkpoint. A participant keeps at most maxHashTicksKept of them (its oldest go first), never one of a
   * desync tick (the evidence of a dispute). Returns the tick's hashes (null when the new one was the oldest to go).
   */
  private storeHash(s: SessionState, playerId: PlayerId, tick: number, hash: string): Map<PlayerId, string> | null {
    let m = s.hashes.get(tick);
    if (!m) s.hashes.set(tick, (m = new Map()));
    m.set(playerId, hash);
    const max = this.d.config.sessions.maxHashTicksKept;
    const mine: number[] = [];
    for (const [t, mm] of s.hashes) if (mm.has(playerId) && !s.desyncTicks.has(t)) mine.push(t);
    if (mine.length > max) {
      mine.sort((a, b) => a - b);
      for (const t of mine.slice(0, mine.length - max)) {
        const mm = s.hashes.get(t)!;
        mm.delete(playerId);
        if (mm.size === 0) s.hashes.delete(t);
      }
    }
    return s.hashes.get(tick) ?? null;
  }

  /** `tick` lies after the closed input of a forced turn whose team `playerId` controls. */
  private forcedOwnerTick(s: SessionState, playerId: PlayerId, tick: number): boolean {
    const t = s.log.turnAt(tick);
    return !!t && t.forcedAt !== undefined && tick > t.forcedAt && this.controller(s, t.team)?.playerId === playerId;
  }

  async finish(playerId: PlayerId, p: PayloadOf<'session.finish'>): Promise<ResultOf<'session.finish'>> {
    const s = this.require(p.sessionId);
    const part = this.participant(s, playerId);
    if (s.info.state === 'ended') return { ended: true, outcome: s.info.outcome, status: 'ended' };
    if (part.finished) {
      if (part.forfeited || !s.reports.has(playerId)) throw new BackendError('INVALID_STATE', 'You already reported the result', { reason: 'already_reported' });
      return { ended: false, outcome: null, status: s.finalizing ? 'verifying' : 'waiting' };
    }
    const now = this.d.clock.now();
    part.finished = true;
    s.reports.set(playerId, { result: p.result, finalHash: p.finalHash ?? null, log: p.log ?? null, at: now });
    s.firstFinishAt ??= now;
    this.bump(s);
    const pending = s.info.participants.filter((x) => !x.finished && !x.forfeited);
    if (pending.length === 0) {
      if (s.info.online && this.simulator) {
        this.startVerification(s);
        return { ended: false, outcome: null, status: 'verifying' };
      }
      await this.finalize(s);
      return { ended: true, outcome: s.info.outcome, status: 'ended' };
    }
    if (!s.solo) void this.persist(s);
    return { ended: false, outcome: null, status: 'waiting' };
  }

  async forfeit(playerId: PlayerId, sessionId: string): Promise<void> {
    const s = this.require(sessionId);
    const part = this.participant(s, playerId);
    this.requireActive(s);
    await this.forfeitParticipant(s, part);
  }

  /** session.forfeit of an active session for one participant (also the account deletion's). */
  private async forfeitParticipant(s: SessionState, part: SessionParticipant): Promise<void> {
    if (part.forfeited) return;
    part.forfeited = true;
    part.finished = true;
    for (const t of part.teams) s.eliminated.add(t);
    this.bump(s);
    if (s.solo) {
      // cancel_fight -> end_game_fn(0)
      await this.finalize(s, { winnerTeam: 0 }, 'forfeited');
      return;
    }
    const remaining = s.info.participants.filter((x) => !x.forfeited);
    if (remaining.length <= 1) {
      const winnerTeam = remaining[0]?.teams.find((t) => !s.eliminated.has(t)) ?? 0;
      await this.finalize(s, { winnerTeam }, 'opponents forfeited');
      return;
    }
    if (part.teams.includes(s.info.currentTeam) && s.log.current.forcedAt === undefined) this.forceTurn(s, 'forfeit');
    void this.persist(s);
    if (remaining.every((x) => x.finished)) {
      if (this.simulator) this.startVerification(s);
      else await this.finalize(s);
    }
  }

  /**
   * session.dispute (see the module comment): the participant's simulation disagrees with the battle. The server
   * re-simulates the log known now (in the background when it has a simulator: status 'resolving', the verdict comes as
   * session.ended); without a simulator nobody can be blamed and the session ends without a winner.
   */
  async dispute(playerId: PlayerId, p: PayloadOf<'session.dispute'>): Promise<ResultOf<'session.dispute'>> {
    const s = this.require(p.sessionId);
    const part = this.participant(s, playerId);
    if (s.info.state === 'ended') return { status: 'ended', outcome: s.info.outcome };
    if (s.solo || !s.info.online) throw new BackendError('INVALID_STATE', 'Only an online battle can be disputed', { reason: 'not_online_battle' });
    if (part.forfeited) throw new BackendError('INVALID_STATE', 'You forfeited this session', { reason: 'forfeited' });
    // A dispute is being decided, or every report is in and the whole battle is being verified: its verdict decides.
    if (s.dispute || s.finalizing) return { status: 'resolving', outcome: null };
    const now = this.d.clock.now();
    const reason = p.reason ?? null;
    s.dispute = { by: playerId, tick: p.tick, reason, at: now, log: s.log.toJSON() };
    this.bump(s);
    this.d.logger.warn('session disputed', { sessionId: s.info.sessionId, by: playerId, tick: p.tick, reason });
    if (!this.simulator) {
      await this.finalize(s, { winnerTeam: 0 }, `desync in tick ${p.tick}: no battle simulator on this server can decide who went out of sync`, { verdict: 'unverified', diverged: [] });
      return { status: 'ended', outcome: s.info.outcome };
    }
    await this.persist(s);
    this.startResolution(s);
    return { status: 'resolving', outcome: null };
  }

  /** Keeps a background verification or dispute resolution of `s` (settled() and the account deletion wait for it). */
  private track(s: SessionState, run: Promise<void>): void {
    this.verifications.add(run);
    s.running = run;
    void run.finally(() => {
      this.verifications.delete(run);
      if (s.running === run) s.running = null;
    });
  }

  /** Decides the session's dispute in the background (shutdown leaves it to the restored session). */
  private startResolution(s: SessionState): void {
    if (s.finalizing || s.info.state !== 'active' || !s.dispute) return;
    s.finalizing = true;
    const run = this.resolveDispute(s).catch(async (err) => {
      this.d.logger.error('session dispute failed', { sessionId: s.info.sessionId, err });
      s.finalizing = false;
      if (!this.stopped && s.info.state === 'active') await this.finalize(s, { winnerTeam: 0 }, 'desync: the dispute could not be decided', { verdict: 'unverified', diverged: [] });
    });
    this.track(s, run);
  }

  private async resolveDispute(s: SessionState): Promise<void> {
    const d = s.dispute!;
    const last = d.log.turns[d.log.turns.length - 1];
    const limit = Math.max(1, last.endTick ?? last.throughTick);
    let sim: SimulationResult;
    try {
      sim = await this.simulateOnline(s, d.log, { maxTicks: limit, checkpoints: true });
    } finally {
      s.finalizing = false;
    }
    if (this.stopped || s.info.state !== 'active') return;
    const moves: SimulatedMove[] | undefined = sim.ok ? sim.moves : sim.error === 'max_ticks' ? sim.moves : undefined;
    if (!moves) {
      const invalid = !sim.ok && sim.error === 'invalid_record';
      const why = sim.ok ? 'the simulator reported no moves' : sim.message;
      await this.finalize(s, { winnerTeam: 0 }, `desync in tick ${d.tick}: ${invalid ? 'the battle cannot be re-simulated' : 'the battle simulator could not decide it'}: ${why}`, {
        verdict: invalid ? 'rejected' : 'unverified',
        diverged: [],
      });
      return;
    }
    // Who the simulation contradicts: the turn structure first, then every checkpoint of a simulated move start.
    const culprits = new Map<PlayerId, string>();
    const violation = findTurnViolation(d.log, moves, { openEnd: last.endTick === null });
    if (violation) {
      const who = this.turnCulprit(s, d.log, violation);
      if (who) culprits.set(who, violation.message);
    }
    for (const m of moves) {
      if (m.hash === undefined) continue;
      for (const [pid, h] of s.hashes.get(m.tick) ?? []) {
        if (h !== m.hash && !culprits.has(pid)) culprits.set(pid, `its checkpoint of tick ${m.tick} differs from the simulated one`);
      }
    }
    // Nothing it disputed differs from the simulation: the disputing participant's own battle went out of sync.
    if (culprits.size === 0) culprits.set(d.by, 'its battle differs from the simulation, nothing it disputed does');
    const remaining = s.info.participants.filter((x) => !culprits.has(x.playerId) && !x.forfeited);
    const winnerTeam = remaining.length === 1 ? (remaining[0].teams.find((t) => !s.eliminated.has(t)) ?? 0) : 0;
    const names = [...culprits.entries()].map(([pid, why]) => `${s.info.participants.find((x) => x.playerId === pid)?.displayName ?? pid} (${why})`);
    await this.finalize(s, { winnerTeam }, `desync in tick ${d.tick}: the server's simulation of the battle contradicts ${names.join(', ')}`, {
      verdict: 'rejected',
      diverged: [...culprits.keys()],
    });
  }

  /**
   * The participant responsible for a turn structure the simulation contradicts: a turn that went on into another
   * team's move is its controller's (it gave the input), unless the move lies after a forced turn's closed input
   * (there the report of the turn's end was wrong); a wrong turn end is its reporter's.
   */
  private turnCulprit(s: SessionState, log: CommandLogData, v: TurnViolation): PlayerId | null {
    const t = log.turns[v.turn];
    if (!t) return null;
    const controller = this.controller(s, t.team)?.playerId ?? null;
    if (v.kind === 'inside' && (t.forcedAt === undefined || v.tick <= t.forcedAt)) return controller;
    return s.turnEnds.get(v.turn) ?? controller;
  }

  /**
   * Forces the current turn of an online session: its input is closed after the confirmed tick E and every later tick
   * of it is empty; the next team is reported by a participant (reportForcedEnd). The report is awaited until the
   * new deadline (turn time + grace); after it any participant may report.
   */
  private forceTurn(s: SessionState, why: string): void {
    const cur = s.log.current;
    if (s.solo || cur.forcedAt !== undefined || cur.endTick !== null) return;
    const E = cur.throughTick;
    const err = s.log.closeInput(cur.turn, E);
    if (err) {
      this.d.logger.error('forced turn failed', { sessionId: s.info.sessionId, err });
      return;
    }
    const now = this.d.clock.now();
    this.sync(s);
    s.forcedSince = now;
    s.lastProgressAt = now;
    s.turnStartedAt = now;
    s.info.deadlineAt = this.deadline(s, now);
    // The owner's checkpoints after E come from input the server dropped (see hash).
    const owner = this.controller(s, cur.team);
    if (owner) for (const [t, m] of s.hashes) if (t > E && m.delete(owner.playerId) && m.size === 0) s.hashes.delete(t);
    const version = this.bump(s);
    this.d.logger.info('turn forced', { sessionId: s.info.sessionId, why, turn: cur.turn, team: cur.team, inputThrough: E });
    this.d.hub.send(this.all(s), 'session.turn', {
      sessionId: s.info.sessionId,
      endedTurn: cur.turn,
      endTick: E,
      turn: cur.turn,
      team: null,
      startTick: null,
      forced: true,
      deadlineAt: s.info.deadlineAt,
      version,
    });
    void this.persist(s);
  }

  // ------------------------------------------------------------------------------------------------------
  // End of a session

  /** Runs finalize in the background (the online verification); shutdown leaves it to the restored session. */
  private startVerification(s: SessionState): void {
    if (s.dispute) return this.startResolution(s);
    if (s.finalizing || s.info.state !== 'active') return;
    const run = this.finalize(s).catch((err) => this.d.logger.error('session finalize failed', { sessionId: s.info.sessionId, err }));
    this.track(s, run);
  }

  /** The participants' consensus: every report has the same winner and final hash. */
  private consensus(s: SessionState): { result: BattleResult | null; finalHash: string | null; agreed: boolean } {
    const reports = [...s.reports.values()];
    if (reports.length === 0) return { result: null, finalHash: null, agreed: false };
    const winner = reports[0].result.winnerTeam;
    const hashes = new Set(reports.map((r) => r.finalHash));
    const agreed = reports.every((r) => r.result.winnerTeam === winner) && hashes.size === 1;
    return { result: { winnerTeam: winner }, finalHash: agreed ? [...hashes][0] : null, agreed };
  }

  /** The online battle's re-simulation, retrying transient simulator failures. */
  private async simulateOnline(s: SessionState, log: CommandLogData, opts: { maxTicks?: number; checkpoints?: boolean } = {}): Promise<SimulationResult> {
    const online = s.info.online!;
    const last = log.turns[log.turns.length - 1];
    const lastTick = last.endTick ?? last.throughTick;
    const maxTicks = opts.maxTicks ?? lastTick + SIM_TICKS_PAST_LOG;
    let r: SimulationResult = { ok: false, error: 'unavailable', message: 'no simulator', durationMs: 0 };
    for (let attempt = 0; attempt <= SIM_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, SIM_RETRY_DELAYS_MS[attempt - 1]));
      if (this.stopped) return r;
      r = await this.simulator!.simulate(
        { init: online, seed: s.info.seed, log },
        { maxTicks, timeoutMs: this.d.config.sessions.simulationTimeoutMs, ...(opts.checkpoints ? { checkpoints: true } : {}) },
      );
      if (r.ok || r.error === 'invalid_record' || r.error === 'max_ticks') return r;
      this.d.logger.warn('online battle simulation failed', { sessionId: s.info.sessionId, attempt: attempt + 1, error: r.error, message: r.message });
    }
    return r;
  }

  private async finalize(
    s: SessionState,
    forced?: BattleResult,
    forcedReason?: string,
    /** A dispute's decision: its verdict and the participants the simulation contradicted. */
    decided?: { verdict: ValidationVerdict; diverged: PlayerId[] },
  ): Promise<void> {
    if (s.info.state === 'ended' || s.finalizing) return;
    s.finalizing = true;
    try {
      const reports = [...s.reports.values()];
      const agreed = this.consensus(s);
      let result: BattleResult = forced ?? agreed.result ?? { winnerTeam: 0 };
      s.log.close(s.log.current.throughTick);
      let log = s.log.toJSON();
      if (s.solo && s.log.commandCount === 0 && reports[0]?.log) log = reports[0].log;
      let verdict: ValidationVerdict;
      let reason: string | null = forcedReason ?? null;
      let verified: boolean;
      let simulated: SessionEndFacts['simulated'] = null;
      const consensus = !forced && agreed.agreed;
      if (s.info.online) {
        if (forced) {
          verdict = decided?.verdict ?? 'unverified';
        } else if (!this.simulator) {
          verdict = 'unverified';
          reason = consensus ? 'no battle simulator on this server' : 'participants reported different results or final hashes';
        } else {
          await this.persist(s);
          const sim = await this.simulateOnline(s, log);
          if (this.stopped) return; // shutdown: the restored session is verified again
          if (sim.ok) {
            result = { winnerTeam: sim.outcome.winner };
            simulated = { winnerTeam: sim.outcome.winner, finalHash: sim.finalHash };
            const turnOrder = checkTurnOwnership(log, sim.moves ?? []);
            if (turnOrder) {
              verdict = 'rejected';
              reason = `the turn order does not match the battle: ${turnOrder}`;
            } else if (consensus && agreed.result!.winnerTeam === sim.outcome.winner && agreed.finalHash === sim.finalHash) {
              verdict = 'verified';
              reason = null;
            } else {
              verdict = 'rejected';
              reason = reports.length === 0 ? 'no participant reported the result' : 'the reported result differs from the re-simulation of the battle';
            }
          } else if (sim.error === 'invalid_record' || sim.error === 'max_ticks') {
            verdict = 'rejected';
            result = { winnerTeam: 0 };
            reason = `the battle cannot be re-simulated: ${sim.message}`;
          } else {
            verdict = 'unverified';
            reason = `the battle simulator failed: ${sim.message}`;
          }
        }
        verified = verdict === 'verified';
      } else {
        const hashes = reports.map((r) => r.finalHash).filter((h): h is string => h !== null);
        let v: Awaited<ReturnType<AuthoritativeValidator['validate']>>;
        try {
          v = await this.validator.validate({
            sessionId: s.info.sessionId,
            init: s.info.init,
            seed: s.info.seed,
            log,
            claimed: result,
            finalHash: hashes[0] ?? null,
            checkpoints: this.checkpoints(s),
          });
        } catch (err) {
          this.d.logger.error('battle validator failed', { sessionId: s.info.sessionId, err });
          v = { verdict: 'unverified', reason: 'validator error' };
        }
        if (v.result) result = v.result;
        verdict = v.verdict;
        verified = verdict === 'verified' && (s.solo || consensus);
        reason = forcedReason ?? v.reason;
        if (!forced && !s.solo && !consensus) reason = 'participants reported different results or final hashes';
      }
      const now = this.d.clock.now();
      // A participant whose account is gone although the account deletion did not take it out of this session (a
      // request of it that was under way created the session after the deletion looked) is anonymized now, as
      // removePlayer does: nothing stored from here on names a deleted account. An account that cannot be read (a
      // storage failure) counts as existing: the battle still ends (logged).
      const gone = new Set<PlayerId>();
      for (const p of [...s.info.participants]) {
        if (p.playerId === DELETED_PLAYER_ID || !(await this.accountGone(s, p.playerId))) continue;
        gone.add(p.playerId);
        this.anonymize(s, p.playerId);
      }
      // The replay belongs to the first participant whose account still exists; a battle of deleted accounts only is
      // stored neither as a replay nor as a battle (nobody is left to see it).
      const owner = s.info.participants.find((p) => p.playerId !== DELETED_PLAYER_ID)?.playerId ?? null;
      let replayId: string | null = null;
      try {
        const init: BattleInit = s.info.online ? { ...s.info.init, params: s.info.online as unknown as JsonValue } : s.info.init;
        const rep =
          owner === null
            ? null
            : await this.replays.save(owner, { mode: s.info.mode, stage: s.info.stage, result, init, seed: s.info.seed, log }, { sessionId: s.info.sessionId, verified, verdict });
        replayId = rep?.replayId ?? null;
      } catch (err) {
        this.d.logger.warn('replay for session not stored', { sessionId: s.info.sessionId, err });
      }
      const diverged = decided?.diverged.map((p) => (gone.has(p) ? DELETED_PLAYER_ID : p));
      const outcome: SessionOutcome = { result, verified, verdict, reason, consensus, replayId, endedAt: now, ...(diverged ? { diverged } : {}) };
      s.info.state = 'ended';
      s.info.outcome = outcome;
      s.info.deadlineAt = null;
      s.info.inputClosedAt = null;
      s.endedAt = now;
      const version = this.bump(s);
      s.dirty = false;
      const battle: BattleDoc | null = owner === null ? null : { sessionId: s.info.sessionId, info: s.info, log };
      await this.writer.schedule(`session:${s.info.sessionId}`, () => [...(battle ? [putOp(C.battles, battle.sessionId, battle)] : []), deleteOp(C.liveSessions, s.info.sessionId)]);
      if (battle) await this.archive.stored(battle);
      this.d.hub.send(this.all(s), 'session.ended', { sessionId: s.info.sessionId, outcome, version });
      this.d.logger.info('session ended', { sessionId: s.info.sessionId, winnerTeam: result.winnerTeam, verdict, consensus, reason });
      const facts: SessionEndFacts = {
        reports: reports.map((r, i) => ({ playerId: [...s.reports.keys()][i], winnerTeam: r.result.winnerTeam, finalHash: r.finalHash, at: r.at })),
        simulated,
        forced: forced !== undefined,
      };
      for (const l of this.endedListeners) {
        try {
          l(s.info, facts);
        } catch (err) {
          this.d.logger.error('session ended listener failed', { sessionId: s.info.sessionId, err });
        }
      }
    } finally {
      s.finalizing = false;
    }
  }

  /** finalize: whether the participant's account no longer exists (false when it cannot be read: logged). */
  private async accountGone(s: SessionState, playerId: PlayerId): Promise<boolean> {
    try {
      return (await this.auth.getAccount(playerId)) === null;
    } catch (err) {
      this.d.logger.error('battle participant account not read: kept as it is', { sessionId: s.info.sessionId, playerId, err });
      return false;
    }
  }

  async outcomeFor(sessionId: string): ReturnType<BattleOutcomeLookup['outcomeFor']> {
    const live = this.sessions.get(sessionId);
    const info = live?.info.state === 'ended' ? live.info : (await this.d.storage.get<BattleDoc>(C.battles, sessionId))?.info;
    if (!info || !info.outcome) return null;
    return {
      playerIds: info.participants.map((p) => p.playerId),
      teamsOf: (pid) => info.participants.find((p) => p.playerId === pid)?.teams ?? [],
      mode: info.mode,
      zone: info.init.zone ?? null,
      winnerTeam: info.outcome.result.winnerTeam,
      verified: info.outcome.verified,
    };
  }

  // ------------------------------------------------------------------------------------------------------
  // Presence and time

  /**
   * A player connected (its first connection) or lost its last connection. A disconnected participant keeps its
   * place: its turns are forced only after the disconnect grace period (see sweep); coming back while it is its turn
   * gives it at least the turn grace to go on.
   */
  setConnected(playerId: PlayerId, connected: boolean): void {
    const now = this.d.clock.now();
    for (const s of this.sessions.values()) {
      if (s.info.state !== 'active') continue;
      const p = s.info.participants.find((x) => x.playerId === playerId);
      if (!p || p.connected === connected) continue;
      p.connected = connected;
      p.disconnectedAt = connected ? null : now;
      if (connected && !s.solo && s.log.current.forcedAt === undefined && p.teams.includes(s.info.currentTeam) && s.info.deadlineAt !== null) {
        s.info.deadlineAt = Math.min(Math.max(s.info.deadlineAt, now + this.d.config.sessions.turnGraceSec * 1000), this.turnCap(s));
      }
      this.bump(s);
    }
  }

  async sweep(now: number): Promise<void> {
    const cfg = this.d.config.sessions;
    for (const s of [...this.sessions.values()]) {
      if (s.info.state === 'ended') {
        if (s.endedAt !== null && now - s.endedAt > cfg.keepEndedMs) this.sessions.delete(s.info.sessionId);
        continue;
      }
      if (s.finalizing) continue;
      if (s.solo) {
        if (now - s.info.createdAt > cfg.soloTtlMs) await this.finalize(s, { winnerTeam: 0 }, 'abandoned');
        else if (s.dirty && now - s.persistedAt >= cfg.persistIntervalMs) void this.persist(s);
        continue;
      }
      const gone = (p: SessionParticipant) => p.forfeited || (!p.connected && p.disconnectedAt !== null && now - p.disconnectedAt > Math.max(cfg.finishGraceMs, this.graceMs()));
      if (s.info.participants.every(gone)) {
        await this.finalize(s, { winnerTeam: 0 }, 'abandoned');
        continue;
      }
      if (s.firstFinishAt !== null && now - s.firstFinishAt > cfg.finishGraceMs) {
        if (this.simulator) this.startVerification(s);
        else await this.finalize(s);
        continue;
      }
      if (s.log.current.forcedAt !== undefined) {
        // Nobody reported the forced turn's end long after the deadline: the battle cannot go on.
        if (s.info.deadlineAt !== null && now > s.info.deadlineAt + cfg.finishGraceMs) await this.finalize(s, { winnerTeam: 0 }, 'abandoned: the end of a forced turn was never reported');
      } else if (s.info.deadlineAt !== null) {
        const owner = this.controller(s, s.info.currentTeam);
        let due = s.info.deadlineAt;
        if (owner && !owner.connected && owner.disconnectedAt !== null) due = Math.max(due, owner.disconnectedAt + this.graceMs());
        // Neither input nor reconnects keep a turn beyond its budget.
        due = Math.min(due, this.turnCap(s));
        if (!owner || owner.forfeited) this.forceTurn(s, 'forfeited team');
        else if (now > due) this.forceTurn(s, owner.connected ? 'turn deadline' : 'owner disconnected');
      }
      if (s.info.state === 'active' && s.dirty && now - s.persistedAt >= cfg.persistIntervalMs) void this.persist(s);
    }
  }
}
